// utils/maintenance.ts
// Logique pure du mode maintenance, partagée par le hook, l'écran et les réglages.

export type MaintenanceScope = "web" | "officiels" | "fans" | "media";

/** Clé TanStack Query de l'état maintenance (ici pour éviter un cycle queryClient ↔ services). */
export const MAINTENANCE_QUERY_KEY = ["maintenance-status"] as const;

export const ALL_MAINTENANCE_SCOPES: readonly MaintenanceScope[] = [
  "web",
  "officiels",
  "fans",
  "media",
] as const;

/** Réponse brute du RPC get_maintenance_status(). */
export interface MaintenanceStatus {
  enabled: boolean;
  /** enabled ET fenêtre starts_at/ends_at courante, calculé côté serveur. */
  effective: boolean;
  block_writes: boolean;
  scope: MaintenanceScope[];
  message_fr: string | null;
  message_ar: string | null;
  starts_at: string | null;
  ends_at: string | null;
  updated_at: string | null;
  /** L'appelant est super_admin : jamais bloqué. */
  exempt: boolean;
  server_time: string | null;
}

export const DEFAULT_MAINTENANCE_STATUS: MaintenanceStatus = {
  enabled: false,
  effective: false,
  block_writes: true,
  scope: ["web", "officiels", "fans"],
  message_fr: null,
  message_ar: null,
  starts_at: null,
  ends_at: null,
  updated_at: null,
  exempt: false,
  server_time: null,
};

/** Normalise le jsonb du RPC (champs manquants, scope non tableau…). */
export function normalizeMaintenanceStatus(raw: unknown): MaintenanceStatus {
  if (!raw || typeof raw !== "object") return DEFAULT_MAINTENANCE_STATUS;
  const r = raw as Record<string, unknown>;
  const scope = Array.isArray(r.scope)
    ? (r.scope.filter((s): s is MaintenanceScope =>
        (ALL_MAINTENANCE_SCOPES as readonly string[]).includes(String(s))
      ) as MaintenanceScope[])
    : DEFAULT_MAINTENANCE_STATUS.scope;
  const str = (v: unknown): string | null =>
    typeof v === "string" && v.length > 0 ? v : null;
  return {
    enabled: r.enabled === true,
    effective: r.effective === true,
    block_writes: r.block_writes !== false,
    scope,
    message_fr: str(r.message_fr),
    message_ar: str(r.message_ar),
    starts_at: str(r.starts_at),
    ends_at: str(r.ends_at),
    updated_at: str(r.updated_at),
    exempt: r.exempt === true,
    server_time: str(r.server_time),
  };
}

/**
 * Faut-il afficher l'écran bloquant à ce client ?
 * Vrai quand la maintenance est effective, vise ce scope, et que l'appelant n'est pas exempté.
 */
export function isMaintenanceBlocking(
  status: MaintenanceStatus | null | undefined,
  scope: MaintenanceScope,
  isExempt: boolean = status?.exempt ?? false
): boolean {
  if (!status || !status.effective) return false;
  if (!status.scope.includes(scope)) return false;
  return !isExempt;
}

/** Maintenance effective pour ce scope, exemption ignorée (bandeau super_admin). */
export function isMaintenanceActiveFor(
  status: MaintenanceStatus | null | undefined,
  scope: MaintenanceScope
): boolean {
  return !!status && status.effective && status.scope.includes(scope);
}

/** Message dans la langue courante, avec repli sur l'autre langue. */
export function pickMaintenanceMessage(
  status: MaintenanceStatus | null | undefined,
  language: string
): string | null {
  if (!status) return null;
  if (language === "ar") return status.message_ar ?? status.message_fr;
  return status.message_fr ?? status.message_ar;
}

/** Le trigger enforce_maintenance() lève 'MAINTENANCE_MODE'. */
export function isMaintenanceModeError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const err = error as Record<string, unknown>;
  const msg = typeof err.message === "string" ? err.message : "";
  return msg.includes("MAINTENANCE_MODE");
}
